import { TypedUseSelectorHook, useDispatch as _useDispatch, useSelector as _useSelector } from "react-redux";

import { initLicensesMapListener } from "@dashboard/devices/api/utils";
import DevicesReducer from "@dashboard/devices/store";
import { downloadsApi } from "@dashboard/downloads/api";
import { filesApi } from "@dashboard/files/api";
import { productsApi } from "@dashboard/products/api";
import ProvisioningsReducer from "@dashboard/provisioning/store";
import { fdroidsApi } from "@dashboard/repositories/api";
import { rolloutsApi } from "@dashboard/rollouts/api";
import { workspacesApi } from "@dashboard/workspaces/api";
import { initWorkspacesCacheListener } from "@dashboard/workspaces/api/utils";
import { combineReducers, configureStore, createListenerMiddleware } from "@reduxjs/toolkit";

import { eventsApi } from "./api/events";
import { initEventsCacheListener } from "./api/events/utils";
import { initUrlMiddleware } from "./helpers/middleware";
import AccountReducer from "./account";
import { AppReducer } from "./app";
import AuthReducer from "./auth";
import BetaReducer from "./beta";

const listenerMiddleware = createListenerMiddleware();

initUrlMiddleware(listenerMiddleware);
initEventsCacheListener(listenerMiddleware);
initWorkspacesCacheListener(listenerMiddleware);
initLicensesMapListener(listenerMiddleware);

const rootReducer = combineReducers({
    app: AppReducer,
    auth: AuthReducer,
    account: AccountReducer,
    devices: DevicesReducer,
    beta: BetaReducer,
    provisionings: ProvisioningsReducer,
    [eventsApi.reducerPath]: eventsApi.reducer,
    [workspacesApi.reducerPath]: workspacesApi.reducer,
    [filesApi.reducerPath]: filesApi.reducer,
    [fdroidsApi.reducerPath]: fdroidsApi.reducer,
    [rolloutsApi.reducerPath]: rolloutsApi.reducer,
    [downloadsApi.reducerPath]: downloadsApi.reducer,
    [productsApi.reducerPath]: productsApi.reducer,
});

const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
        }).concat(
            listenerMiddleware.middleware,
            eventsApi.middleware,
            workspacesApi.middleware,
            filesApi.middleware,
            fdroidsApi.middleware,
            rolloutsApi.middleware,
            downloadsApi.middleware,
            productsApi.middleware,
        ),
    devTools: import.meta.env.DEV,
});

export const setupStore = (preloadedState?: RootState) => {
    return configureStore({
        reducer: rootReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware().concat(
                listenerMiddleware.middleware,
                eventsApi.middleware,
                workspacesApi.middleware,
                filesApi.middleware,
                fdroidsApi.middleware,
                rolloutsApi.middleware,
                downloadsApi.middleware,
                productsApi.middleware,
            ),
        preloadedState,
    });
};

export default store;

export type RootState = ReturnType<typeof store.getState>;

export type AppStore = ReturnType<typeof setupStore>;

type AppDispatch = typeof store.dispatch;

export const useDispatch = (): AppDispatch => _useDispatch<AppDispatch>();

export const useSelector: TypedUseSelectorHook<RootState> = _useSelector;
